import { formatUsdValue } from '@repo/utils'
import { type Address, type Client, erc20Abi, formatUnits, getContract, isAddressEqual } from 'viem'

import type { Token } from '../web3.types'

import { getChainConfig, isChainId } from './chain.utils'

/**
 * Fetches ERC20 metadata using a batch method.
 * @param client - Public Client for blockchain interaction
 * @param address - Token contract address
 * @returns Token object containing name, symbol, decimals, and address
 */
export async function getToken(client: Client, address: Address): Promise<Token> {
  const tokenContract = getContract({
    abi: erc20Abi,
    address,
    client
  })

  const [name, symbol, decimals] = await Promise.all([
    tokenContract.read.name(),
    tokenContract.read.symbol(),
    tokenContract.read.decimals()
  ])

  return {
    address,
    name,
    symbol,
    decimals
  }
}

/**
 * `formatUsdValue` with a sub-cent floor: nonzero magnitudes below $0.01 render as `< $0.01`
 * instead of rounding to `$0`. Local to the token input/select display surfaces.
 */
export function formatUsdDisplay(value: number): string {
  return value !== 0 && Math.abs(value) < 0.01 ? '< $0.01' : formatUsdValue(value)
}

export function computeTokenAmountInUsd(
  amount: bigint,
  decimals: number,
  priceUsd?: number
): number | undefined {
  if (!priceUsd) {
    return undefined
  }

  return Number(formatUnits(amount, decimals)) * priceUsd
}

/**
 * Checks whether a token requires setting allowance to 0 before approving a new non-zero amount.
 * Mirrors `APPROVE_ONLY_ONCE_TOKENS` from `@morpho-org/simulation-sdk`.
 */
export function isResetAllowanceBeforeApproveToken(
  chainId: number,
  tokenAddress: Address
): boolean {
  // Unsupported chains have no token list of their own, and getChainConfig throws for them.
  if (!isChainId(chainId)) {
    return false
  }

  const config = getChainConfig(chainId)
  const list =
    'resetAllowanceBeforeApproveTokens' in config
      ? config.resetAllowanceBeforeApproveTokens
      : undefined

  return list?.some(address => isAddressEqual(address, tokenAddress)) ?? false
}

export type GroupedToken = Token & { addresses: Address[] }

/**
 * Groups tokens by their symbol, collecting all addresses for each token group.
 * Useful for cross-chain scenarios where the same token exists on multiple chains.
 *
 * @param tokens - Array of tokens to group
 * @returns Array of grouped tokens with addresses array containing all addresses for that token
 */
export function groupTokensBySymbol(tokens: Token[]): GroupedToken[] {
  const tokensBySymbol = new Map<string, GroupedToken>()

  tokens.forEach(token => {
    const { symbol, name, decimals, address } = token

    // Only process tokens that have required fields
    if (symbol && name && decimals) {
      const key = symbol.toLowerCase()

      const existingToken = tokensBySymbol.get(key)
      if (existingToken) {
        // Add this address to the existing token group
        if (!existingToken.addresses.includes(address.toLowerCase() as Address)) {
          existingToken.addresses.push(address.toLowerCase() as Address)
        }
      } else {
        // Create new token group
        tokensBySymbol.set(key, {
          ...token,
          address: address as Address, // Keep first address as primary
          addresses: [address.toLowerCase() as Address]
        })
      }
    }
  })

  return Array.from(tokensBySymbol.values()).sort((a, b) => a.symbol.localeCompare(b.symbol))
}

/**
 * Filters items based on grouped token selection.
 * When a token group is selected, all addresses in that group are considered valid.
 *
 * @param items - Array of items to filter
 * @param selectedTokens - Array of selected token addresses
 * @param groupedTokens - Array of grouped tokens with addresses
 * @param getTokenAddress - Function to extract token address from an item
 * @returns Filtered array of items
 */

type FilterByGroupedTokensParams<T> = {
  items: T[]
  selectedTokens: Address[]
  groupedTokens: (Token & { addresses: Address[] })[]
  getTokenAddress: (item: T) => Address
}

export function filterByGroupedTokens<T>({
  items,
  selectedTokens,
  groupedTokens,
  getTokenAddress
}: FilterByGroupedTokensParams<T>): T[] {
  if (selectedTokens.length === 0) {
    return items
  }
  // Get all addresses for selected token groups
  const selectedTokenAddresses = new Set<string>()
  selectedTokens.forEach(selectedToken => {
    const tokenGroup = groupedTokens.find(token =>
      token.addresses.some(addr => isAddressEqual(addr, selectedToken))
    )
    if (tokenGroup) {
      tokenGroup.addresses.forEach(addr => selectedTokenAddresses.add(addr.toLowerCase()))
    }
  })
  return items.filter(item => selectedTokenAddresses.has(getTokenAddress(item).toLowerCase()))
}
